import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import { Field, Form } from '@hrlogics/react-components/form';

import { useGenerateYearToDateFinancialReportMutation } from 'src/api';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { DateCalendarField } from '../../finalForm/DateCalendarField';

import { DashboardYTDFinancialReportFormField } from './constants';
import { DashboardYTDFinancialReportFormValues } from './types';

type DashboardYTDFinancialReportFormProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
};

export const DashboardYTDFinancialReportForm: React.FC<DashboardYTDFinancialReportFormProps> = ({
  onCloseModal,
  switchClosability,
}) => {
  const [generateYearToDateFinancialReport] = useGenerateYearToDateFinancialReportMutation();
  const initialValues = React.useMemo(getInitialValues, []);

  const handleSubmit = React.useCallback(
    async (formValues: DashboardYTDFinancialReportFormValues) => {
      const date = formValues.date.toISODate();
      if (!date) return;

      switchClosability(false);

      const { data } = await generateYearToDateFinancialReport({
        variables: { date },
      });

      if (data?.generateYearToDateFinancialReport) {
        downloadFileByDownloadUrl(
          data.generateYearToDateFinancialReport.downloadUrl,
          data.generateYearToDateFinancialReport.filename,
        );
      }

      switchClosability(true);
      onCloseModal();
    },
    [generateYearToDateFinancialReport, onCloseModal, switchClosability],
  );

  return (
    <React.Fragment>
      <Typography variant="body1" sx={theme => ({ color: theme.palette.text.secondary })}>
        Please select the date for your report. The report will include data from January of the
        selected year through the end of the chosen date.
      </Typography>

      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ submitting: true }}
      >
        {({ submitting }) => (
          <React.Fragment>
            <Field
              component={DateCalendarField}
              name={DashboardYTDFinancialReportFormField.date}
              minDate={MIN_DATE}
              disableFuture
            />

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                variant="outlined"
                color="error"
                onClick={() => onCloseModal()}
                disabled={submitting}
              >
                Close
              </Button>

              <Button type="submit" variant="contained" disabled={submitting} loading={submitting}>
                Generate report
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </React.Fragment>
  );
};

const MIN_DATE = DateTime.fromISO('2023-09-01');

const getInitialValues = (): DashboardYTDFinancialReportFormValues => ({
  date: DateTime.now().startOf('day'),
});
